class VideoParallaxModal {
  constructor(root) {
    this.root = root;
    this.modal = root.querySelector('[data-video-modal]');
    this.dialog = root.querySelector('[data-video-dialog]');
    this.openButton = root.querySelector('[data-video-open]');
    this.closeButtons = Array.from(root.querySelectorAll('[data-video-close]'));
    this.videoElement = root.querySelector('[data-video-element]');
    this.iframeElement = root.querySelector('[data-video-iframe]');
    this.sourceType = root.dataset.sourceType || 'mp4';
    this.externalUrl = root.dataset.externalUrl || '';
    this.lastFocusedElement = null;
    this.focusableNodes = [];

    this.onOpenClick = this.onOpenClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  init() {
    if (!this.root || !this.modal || !this.dialog || !this.openButton) {
      return;
    }

    this.openButton.addEventListener('click', this.onOpenClick);
    this.closeButtons.forEach((btn) => btn.addEventListener('click', this.onCloseClick));
    document.addEventListener('keydown', this.onKeydown);
  }

  destroy() {
    this.openButton?.removeEventListener('click', this.onOpenClick);
    this.closeButtons.forEach((btn) => btn.removeEventListener('click', this.onCloseClick));
    document.removeEventListener('keydown', this.onKeydown);

    this.closeModal({ restoreFocus: false });
  }

  onOpenClick() {
    this.openModal();
  }

  onCloseClick() {
    this.closeModal();
  }

  onKeydown(event) {
    if (this.modal.hidden) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  openModal() {
    if (this.modal.hidden === false) {
      return;
    }

    this.lastFocusedElement = document.activeElement;
    this.modal.hidden = false;
    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
    });

    this.openButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('video-parallax-lock');

    this.setupVideoPlayback();
    this.cacheFocusableNodes();
    this.dialog.focus();
  }

  closeModal({ restoreFocus = true } = {}) {
    if (this.modal.hidden) {
      return;
    }

    this.modal.classList.remove('is-open');
    this.modal.hidden = true;

    this.openButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('video-parallax-lock');

    this.pauseAndResetVideo();

    if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
      this.lastFocusedElement.focus();
    }
  }

  cacheFocusableNodes() {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    this.focusableNodes = Array.from(this.dialog.querySelectorAll(selector)).filter(
      (node) => node instanceof HTMLElement && node.offsetParent !== null
    );
  }

  trapFocus(event) {
    if (!this.focusableNodes.length) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }

    const first = this.focusableNodes[0];
    const last = this.focusableNodes[this.focusableNodes.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  setupVideoPlayback() {
    if (this.sourceType === 'mp4') {
      if (this.videoElement) {
        this.videoElement.currentTime = 0;
        const playPromise = this.videoElement.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }
      return;
    }

    if (!this.iframeElement || !this.externalUrl) {
      return;
    }

    const embedUrl = this.buildEmbedUrl(this.externalUrl, this.sourceType);
    if (embedUrl) {
      this.iframeElement.src = embedUrl;
    }
  }

  pauseAndResetVideo() {
    if (this.sourceType === 'mp4') {
      if (this.videoElement) {
        this.videoElement.pause();
        const shouldReset = this.root.dataset.resetMp4OnClose !== 'false';
        if (shouldReset) {
          this.videoElement.currentTime = 0;
        }
      }
      return;
    }

    if (this.iframeElement) {
      this.iframeElement.src = '';
    }
  }

  buildEmbedUrl(rawUrl, sourceType) {
    if (!rawUrl) {
      return '';
    }

    try {
      const parsed = new URL(rawUrl, window.location.origin);

      if (sourceType === 'youtube') {
        let videoId = '';
        if (parsed.hostname.includes('youtu.be')) {
          videoId = parsed.pathname.replace('/', '');
        } else {
          videoId = parsed.searchParams.get('v') || '';
        }
        if (!videoId) {
          return '';
        }
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
      }

      if (sourceType === 'vimeo') {
        const parts = parsed.pathname.split('/').filter(Boolean);
        const videoId = parts[parts.length - 1] || '';
        if (!videoId) {
          return '';
        }
        return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
      }
    } catch (error) {
      return '';
    }

    return '';
  }
}

// Native parallax — no pixelcog/jQuery.
// The theme scrolls inside `.page-wrapper` on desktop (html/body overflow:hidden),
// so a position:fixed mirror attached to body (what parallax.js does) ends up
// behind opaque section backgrounds and mis-synced with scroll, which shows up
// in Safari as a missing thumbnail and a weird gap while scrolling.
const MOBILE_QUERY = '(max-width: 768px)';
const DESKTOP_SCROLL_QUERY = '(min-width: 990px)';
// Oversized media layer. Higher = more room to travel = stronger parallax.
// Keep clamped so overflow:hidden never shows empty edges.
const PARALLAX_SCALE = 1.55;

const modalInstances = new Map();
const parallaxInstances = new Map();

let rafId = 0;
let scrollTarget = null;
let scrollBound = false;
let resizeBound = false;
let mediaQueryBound = false;

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isMobileViewport() {
  return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches;
}

function getScrollTarget() {
  if (typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_SCROLL_QUERY).matches) {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper instanceof HTMLElement) {
      return pageWrapper;
    }
  }
  return window;
}

function getViewportHeight(target) {
  if (target instanceof HTMLElement) {
    return target.clientHeight || window.innerHeight || 1;
  }
  return window.innerHeight || 1;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function parseSpeed(node) {
  const raw = Number.parseFloat(node.dataset.speed || '0.5');
  if (Number.isNaN(raw)) {
    return 0.5;
  }
  return clamp(raw, 0.05, 1);
}

function ensureMedia(node) {
  let media = node.querySelector('[data-video-parallax-media]');
  if (media instanceof HTMLElement) {
    return media;
  }

  media = document.createElement('div');
  media.className = 'video-parallax__media';
  media.setAttribute('data-video-parallax-media', '');
  media.setAttribute('aria-hidden', 'true');
  node.insertBefore(media, node.firstChild);
  return media;
}

function applyMediaImage(node, media) {
  const imageSrc = node.dataset.imageSrc || '';
  if (!imageSrc) {
    media.style.backgroundImage = '';
    return;
  }

  media.style.backgroundImage = `url("${imageSrc}")`;
}

function setStaticBackground(node, media) {
  media.style.transform = 'translate3d(0, 0, 0) scale(1)';
  node.classList.remove('video-parallax--parallax-active');
}

function setParallaxReady(node) {
  node.classList.add('video-parallax--parallax-active');
  node.classList.remove('video-parallax--no-plugin');
}

function updateParallaxInstance(node) {
  const state = parallaxInstances.get(node);
  if (!state || !state.media) {
    return;
  }

  const { media, speed } = state;

  if (!node.dataset.imageSrc) {
    setStaticBackground(node, media);
    return;
  }

  if (isMobileViewport() || prefersReducedMotion()) {
    setStaticBackground(node, media);
    node.classList.remove('video-parallax--no-plugin');
    return;
  }

  setParallaxReady(node);

  const rect = node.getBoundingClientRect();
  const viewportHeight = getViewportHeight(scrollTarget);
  const sectionHeight = rect.height || node.offsetHeight || 1;

  // Shift range must stay inside the oversized media layer so we never expose
  // empty space above/below the image (the "weird gap while scrolling" bug).
  const overflow = sectionHeight * (PARALLAX_SCALE - 1);
  const maxShift = overflow / 2;

  // Pixelcog-like: image lags behind scroll as the section crosses the viewport.
  // delta is px from viewport center → section center; multiply by speed so the
  // CMS "parallax speed" control still matters (0.5 ≈ half of scroll distance).
  const sectionCenter = rect.top + sectionHeight / 2;
  const viewportCenter = viewportHeight / 2;
  const delta = sectionCenter - viewportCenter;
  const shift = clamp(-delta * speed, -maxShift, maxShift);

  media.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0) scale(${PARALLAX_SCALE})`;
}

function tickParallax() {
  rafId = 0;
  parallaxInstances.forEach((_, node) => {
    if (node.isConnected) {
      updateParallaxInstance(node);
    }
  });
}

function requestParallaxTick() {
  if (rafId) {
    return;
  }
  rafId = window.requestAnimationFrame(tickParallax);
}

function bindScrollTarget() {
  const nextTarget = getScrollTarget();

  if (scrollBound && scrollTarget === nextTarget) {
    return;
  }

  if (scrollBound && scrollTarget) {
    scrollTarget.removeEventListener('scroll', requestParallaxTick);
  }

  scrollTarget = nextTarget;
  scrollTarget.addEventListener('scroll', requestParallaxTick, { passive: true });
  scrollBound = true;
}

function bindGlobalListeners() {
  if (!resizeBound) {
    resizeBound = true;
    let resizeTimeout;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          bindScrollTarget();
          requestParallaxTick();
        }, 100);
      },
      { passive: true }
    );
  }

  if (!mediaQueryBound && typeof window.matchMedia === 'function') {
    mediaQueryBound = true;
    const desktopQuery = window.matchMedia(DESKTOP_SCROLL_QUERY);
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const onChange = () => {
      bindScrollTarget();
      requestParallaxTick();
    };

    if (typeof desktopQuery.addEventListener === 'function') {
      desktopQuery.addEventListener('change', onChange);
      mobileQuery.addEventListener('change', onChange);
    } else if (typeof desktopQuery.addListener === 'function') {
      desktopQuery.addListener(onChange);
      mobileQuery.addListener(onChange);
    }
  }
}

function registerParallax(node) {
  const media = ensureMedia(node);
  applyMediaImage(node, media);

  parallaxInstances.set(node, {
    media,
    speed: parseSpeed(node),
  });

  node.classList.remove('video-parallax--no-plugin');
  updateParallaxInstance(node);
}

function unregisterParallax(node) {
  parallaxInstances.delete(node);
}

function initVideoParallax(scope) {
  bindScrollTarget();
  bindGlobalListeners();

  const root = scope instanceof HTMLElement || scope instanceof Document ? scope : document;
  const selector = '[data-video-parallax]';
  const nodes =
    root instanceof HTMLElement && root.matches(selector)
      ? [root]
      : Array.from(root.querySelectorAll(selector));

  nodes.forEach((node) => {
    registerParallax(node);

    if (!modalInstances.has(node)) {
      const instance = new VideoParallaxModal(node);
      instance.init();
      modalInstances.set(node, instance);
    }
  });

  requestParallaxTick();
}

function destroyVideoParallax(scope) {
  if (!(scope instanceof HTMLElement)) {
    return;
  }

  const nodes = scope.querySelectorAll('[data-video-parallax]');
  nodes.forEach((node) => {
    const instance = modalInstances.get(node);
    if (instance) {
      instance.destroy();
      modalInstances.delete(node);
    }
    unregisterParallax(node);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initVideoParallax(document));
} else {
  initVideoParallax(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initVideoParallax(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyVideoParallax(event.target);
});
